import type { BadgeProps } from '@/components/ui/badge'
import type {
  FeedbackElicitation,
  FeedbackKind,
  FeedbackPromptScope,
  FeedbackStatus,
  FeedbackVisibility,
} from '@/hooks/use-feedback'

type BadgeVariant = NonNullable<BadgeProps['variant']>

export const FEEDBACK_KINDS: { value: FeedbackKind; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'change_request', label: 'Change request' },
  { value: 'agent_performance', label: 'Agent performance' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'general', label: 'General' },
]

export const FEEDBACK_STATUSES: { value: FeedbackStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'declined', label: 'Declined' },
  { value: 'duplicate', label: 'Duplicate' },
]

export const FEEDBACK_PROMPT_SCOPES: { value: FeedbackPromptScope; label: string }[] = [
  { value: 'board', label: 'Board (shared)' },
  { value: 'onboarding', label: 'Onboarding (private)' },
]

const KIND_LABELS = Object.fromEntries(FEEDBACK_KINDS.map((k) => [k.value, k.label]))
const STATUS_LABELS = Object.fromEntries(FEEDBACK_STATUSES.map((s) => [s.value, s.label]))

export function formatFeedbackKind(kind: string): string {
  return KIND_LABELS[kind] ?? kind
}

export function formatFeedbackStatus(status: string): string {
  return STATUS_LABELS[status] ?? status
}

export function formatFeedbackPromptScope(scope: string | undefined): string {
  if (scope === 'onboarding') return 'Onboarding'
  return 'Board'
}

export function feedbackKindBadgeVariant(kind: FeedbackKind): BadgeVariant {
  switch (kind) {
    case 'bug':
      return 'destructiveSoft'
    case 'feature_request':
      return 'defaultSoft'
    case 'change_request':
      return 'warningSoft'
    case 'agent_performance':
      return 'promotionSoft'
    case 'onboarding':
      return 'secondarySoft'
    default:
      return 'secondarySoft'
  }
}

export function feedbackStatusBadgeVariant(status: FeedbackStatus): BadgeVariant {
  switch (status) {
    case 'done':
      return 'successSoft'
    case 'in_progress':
    case 'planned':
      return 'defaultSoft'
    case 'acknowledged':
      return 'secondarySoft'
    case 'declined':
    case 'duplicate':
      return 'outline'
    default:
      return 'secondarySoft'
  }
}

export function formatFeedbackElicitation(elicitation: FeedbackElicitation): string | null {
  switch (elicitation) {
    case 'prompted':
      return 'Prompted'
    case 'derived':
      return 'From a rating'
    default:
      // Volunteered is the norm — no badge noise.
      return null
  }
}

export function formatFeedbackVisibility(visibility: FeedbackVisibility | undefined): string | null {
  if (visibility === 'platform') return 'Platform only'
  return null
}
